import { Matrix, Point } from './../math';
import { Rotation } from './Rotation';
import { Scale } from './Scale';
import { Translation } from './Translation';
/**
 * A composite transformation that combines translation, rotation, and scaling operations.
 * Provides methods to apply multiple transformations as a single matrix operation.
 */
export class CompositeTransform {
    /**
     * Creates a new CompositeTransform instance.
     * @param x The x translation offset
     * @param y The y translation offset
     * @param scaleX The x scaling factor
     * @param scaleY The y scaling factor
     * @param angle The rotation angle in radians
     * @param center The center point for rotation
     */
    constructor(x, y, scaleX, scaleY, angle, center) {
        this.translate = new Translation(x, y);
        if (scaleX !== undefined && scaleY !== undefined) {
            this.scale = new Scale(scaleX, scaleY);
        }
        if (angle !== undefined) {
            this.rotate = center ? new Rotation(angle, center.x, center.y) : new Rotation(angle);
        }
    }
    /**
     * Returns a string representation of the composite transformation.
     * @returns String representation of all transformations
     */
    toString() {
        const toString = function (transform) {
            return transform ? transform.toString() : '';
        };
        return toString(this.translate) +
            toString(this.rotate) +
            toString(this.scale);
    }
    /**
     * Renders the transformation by applying it to a visual element.
     * @param visual The visual element to apply the transformation to
     */
    render(visual) {
        visual._transform = this;
        visual._renderTransform();
    }
    /**
     * Converts the composite transformation to a matrix representation.
     * @returns A matrix representing the combined transformations
     */
    toMatrix() {
        let m = Matrix.unit();
        if (this.translate) {
            m = m.times(this.translate.toMatrix());
        }
        if (this.rotate) {
            m = m.times(this.rotate.toMatrix());
        }
        if (this.scale) {
            m = m.times(this.scale.toMatrix());
        }
        return m;
    }
    /**
     * Creates an inverted version of this composite transformation.
     * @returns A new CompositeTransform that represents the inverse transformation
     */
    invert() {
        const rotate = this.rotate ? this.rotate.invert() : undefined, rotateMatrix = rotate ? rotate.toMatrix() : Matrix.unit(), scale = this.scale ? this.scale.invert() : undefined, scaleMatrix = scale ? scale.toMatrix() : Matrix.unit();
        let translatePoint = new Point(-this.translate.x, -this.translate.y);
        translatePoint = rotateMatrix.times(scaleMatrix).apply(translatePoint);
        const translate = new Translation(translatePoint.x, translatePoint.y);
        const transform = new CompositeTransform();
        transform.translate = translate;
        transform.rotate = rotate;
        transform.scale = scale;
        return transform;
    }
}
